import Alpine from 'alpinejs';

import {
  buildFilterTags,
  type FilterTag,
  type FilterTagId,
  type FilterTagLabels,
} from '../catalogue/filter-tags';
import {
  getPagination,
  type PaginationButton,
} from '../catalogue/get-pagination';
import {
  type CatalogueSelection,
  type CatalogueUniverses,
  type CatalogueView,
  CATALOGUE_VIEWS,
  compareCatalogueStart,
  inCatalogueView,
  matchesCatalogue,
} from '../catalogue/match';
import type { IndexedPreEvent } from '../catalogue/parse';
import { parseCatalogue } from '../catalogue/parse';
import {
  buildCityTree,
  buildOptions,
  type CatalogueCopy,
  type CatalogueTeaser,
  type CountryBranch,
  cityTreeKeys,
  type FilterOption,
  parseCopy,
  tagLabelsFor,
  toTeaser,
} from '../catalogue/present';
import type { DatePreset } from '../catalogue/start-date-window';
import { DATE_PRESETS } from '../catalogue/start-date-window';
import { formatWhen, monthGroupLabel, parseWallClock } from '../catalogue/wall-clock';
import { isMotionReduce } from '../motion';

const PAGE_SIZE = 10;
const CATALOGUE_MD_QUERY = '(min-width: 48rem)';

type FilterSnapshot = CatalogueSelection & { placeQuery: string };

type CatalogueRefs = {
  filtersPanel: HTMLElement;
  filtersOpenBtn: HTMLButtonElement;
  upcoming: HTMLButtonElement;
  past: HTMLButtonElement;
};

type CatalogueMagic = {
  $refs: CatalogueRefs;
  $root: HTMLElement;
  $nextTick: (callback: () => void) => void;
};

export type PreEventCatalogueData = {
  view: CatalogueView;
  theme: string;
  types: string[];
  attendance: string[];
  access: string[];
  languages: string[];
  cities: string[];
  datePreset: DatePreset;
  dateDay: string;
  placeQuery: string;
  page: number;
  ready: boolean;
  viewEmpty: boolean;
  filterEmpty: boolean;
  showPagination: boolean;
  pageCount: number;
  pastCount: number;
  upcomingCount: number;
  pagination: PaginationButton[];
  filterTags: FilterTag[];
  filtersOpen: boolean;
  pageEvents: CatalogueTeaser[];
  themeOptions: FilterOption[];
  typeOptions: FilterOption[];
  attendanceOptions: FilterOption[];
  accessOptions: FilterOption[];
  languageOptions: FilterOption[];
  cityTree: CountryBranch[];
  init(this: PreEventCatalogueData & CatalogueMagic): Promise<void>;
  setView(this: PreEventCatalogueData & CatalogueMagic, view: CatalogueView): void;
  onFilterChange(this: PreEventCatalogueData): void;
  openFilters(this: PreEventCatalogueData & CatalogueMagic): void;
  cancelFilters(this: PreEventCatalogueData & CatalogueMagic): void;
  applyFilters(this: PreEventCatalogueData & CatalogueMagic): void;
  trapFiltersFocus(this: PreEventCatalogueData & CatalogueMagic, event: KeyboardEvent): void;
  toggleCountry(this: PreEventCatalogueData, country: string): void;
  countryState(this: PreEventCatalogueData, country: string): 'true' | 'false' | 'mixed';
  placeSearchHit(this: PreEventCatalogueData, text: string | undefined): boolean;
  removeFilterTag(this: PreEventCatalogueData, id: FilterTagId): void;
  resetFilters(this: PreEventCatalogueData): void;
  setPage(this: PreEventCatalogueData, page: number): void;
  onViewKeydown(this: PreEventCatalogueData & CatalogueMagic, event: KeyboardEvent): void;
};

const catalogueMd = (): MediaQueryList => window.matchMedia(CATALOGUE_MD_QUERY);

export const registerPreEventCatalogue = (): void => {
  Alpine.data('preEventCatalogue', (): PreEventCatalogueData => {
    let indexed: IndexedPreEvent[] = [];
    let copy: CatalogueCopy | undefined;
    let countryCities: Record<string, string[]> = {};
    let universes: CatalogueUniverses = {
      types: [],
      attendance: [],
      access: [],
      languages: [],
    };
    let tagLabels: FilterTagLabels | undefined;
    let filterSnapshot: FilterSnapshot | undefined;

    const refresh = (current: PreEventCatalogueData): void => {
      const now = Date.now();
      const selection = selectionOf(current);
      const inView = indexed.filter((item) =>
        inCatalogueView(item.record, current.view, now),
      );
      const filtered = inView
        .filter((item) => matchesCatalogue(item.record, selection, universes))
        .sort((a, b) => compareCatalogueStart(a.record, b.record, current.view));

      const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
      if (current.page > pageCount) {
        current.page = pageCount;
      }

      const pageStart = (current.page - 1) * PAGE_SIZE;
      current.pageCount = pageCount;
      current.pageEvents = teasersFor(
        filtered.slice(pageStart, pageStart + PAGE_SIZE),
        copy,
      );
      current.viewEmpty = inView.length === 0;
      current.filterEmpty = inView.length > 0 && filtered.length === 0;
      current.upcomingCount =
        current.view === 'upcoming'
          ? inView.length
          : indexed.length - inView.length;
      current.pastCount = indexed.length - current.upcomingCount;
      current.showPagination = Math.ceil(filtered.length / PAGE_SIZE) > 1;
      current.pagination = getPagination(filtered.length, current.page, PAGE_SIZE);
      current.filterTags =
        tagLabels == null
          ? []
          : buildFilterTags({
              selection,
              universes,
              countryCities,
              labels: tagLabels,
            });
    };

    const commitIfLive = (current: PreEventCatalogueData): void => {
      if (current.filtersOpen) {
        return;
      }

      current.page = 1;
      refresh(current);
    };

    const closeFiltersUi = (current: PreEventCatalogueData & CatalogueMagic): void => {
      current.filtersOpen = false;
      document.body.style.overflow = '';
      current.$nextTick(() => {
        current.$refs.filtersOpenBtn.focus();
      });
    };

    return {
      view: 'upcoming',
      theme: 'all',
      types: [],
      attendance: [],
      access: [],
      languages: [],
      cities: [],
      datePreset: 'all',
      dateDay: '',
      placeQuery: '',
      page: 1,
      ready: false,
      viewEmpty: false,
      filterEmpty: false,
      showPagination: false,
      pageCount: 1,
      pastCount: 0,
      upcomingCount: 0,
      pagination: [],
      filterTags: [],
      filtersOpen: false,
      pageEvents: [],
      themeOptions: [],
      typeOptions: [],
      attendanceOptions: [],
      accessOptions: [],
      languageOptions: [],
      cityTree: [],

      async init() {
        const media = catalogueMd();
        media.addEventListener('change', () => {
          if (media.matches && this.filtersOpen) {
            this.applyFilters();
          }
        });

        copy = parseCopy(
          this.$root.querySelector('[data-catalogue-copy]')?.textContent ?? '',
        );
        if (copy == null) {
          this.ready = true;
          refresh(this);
          return;
        }

        try {
          const response = await fetch(copy.endpoint);
          if (!response.ok) {
            throw new Error('The Pre-Event Catalogue could not be loaded.');
          }

          indexed = parseCatalogue(await response.json());
        } catch {
          indexed = [];
        }

        const options = buildOptions(indexed, copy.lang);
        const tree = buildCityTree(indexed, copy.lang, copy.noCountry);
        universes = {
          types: options.types.map((option) => option.value),
          attendance: options.attendance.map((option) => option.value),
          access: options.access.map((option) => option.value),
          languages: options.languages.map((option) => option.value),
        };
        countryCities = cityTreeKeys(tree);
        tagLabels = tagLabelsFor(indexed, tree, copy);
        this.themeOptions = options.themes;
        this.typeOptions = options.types;
        this.attendanceOptions = options.attendance;
        this.accessOptions = options.access;
        this.languageOptions = options.languages;
        this.cityTree = tree;
        this.ready = true;
        refresh(this);
      },

      setView(view) {
        this.view = view;
        this.page = 1;
        refresh(this);
      },

      onFilterChange() {
        commitIfLive(this);
      },

      openFilters() {
        if (this.filtersOpen || catalogueMd().matches) {
          return;
        }

        filterSnapshot = snapshotOf(this);
        this.filtersOpen = true;
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => {
          this.$refs.filtersPanel
            .querySelector<HTMLElement>('[data-filters-close]')
            ?.focus();
        });
      },

      cancelFilters() {
        if (!this.filtersOpen) {
          return;
        }

        if (filterSnapshot != null) {
          restoreSnapshot(this, filterSnapshot);
          filterSnapshot = undefined;
        }

        closeFiltersUi(this);
      },

      applyFilters() {
        if (!this.filtersOpen) {
          return;
        }

        filterSnapshot = undefined;
        this.page = 1;
        refresh(this);
        closeFiltersUi(this);
      },

      trapFiltersFocus(event) {
        if (!this.filtersOpen || event.key !== 'Tab') {
          return;
        }

        const focusable = [
          ...this.$refs.filtersPanel.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ];
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (first == null || last == null) {
          return;
        }

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
          return;
        }

        if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      },

      toggleCountry(country) {
        const keys = countryCities[country] ?? [];
        if (keys.length === 0) {
          return;
        }

        const selected = new Set(this.cities);
        const allOn = keys.every((key) => selected.has(key));
        this.cities = allOn
          ? this.cities.filter((key) => !keys.includes(key))
          : [...new Set([...this.cities, ...keys])];
        commitIfLive(this);
      },

      countryState(country) {
        const keys = countryCities[country] ?? [];
        const count = keys.filter((key) => this.cities.includes(key)).length;
        if (keys.length === 0 || count === 0) {
          return 'false';
        }

        return count === keys.length ? 'true' : 'mixed';
      },

      placeSearchHit(text) {
        const query = this.placeQuery.trim().toLowerCase();
        if (query === '') {
          return true;
        }

        return (text ?? '').toLowerCase().includes(query);
      },

      removeFilterTag(id) {
        switch (id.kind) {
          case 'theme':
            this.theme = 'all';
            break;
          case 'date':
            this.datePreset = 'all';
            this.dateDay = '';
            break;
          case 'place': {
            const keys = new Set(countryCities[id.country] ?? []);
            this.cities = this.cities.filter((key) => !keys.has(key));
            break;
          }
          case 'type':
            this.types = this.types.filter((value) => value !== id.code);
            break;
          case 'attendance':
            this.attendance = this.attendance.filter((value) => value !== id.code);
            break;
          case 'access':
            this.access = this.access.filter((value) => value !== id.code);
            break;
          case 'language':
            this.languages = this.languages.filter((value) => value !== id.code);
            break;
        }

        this.page = 1;
        refresh(this);
      },

      resetFilters() {
        this.theme = 'all';
        this.types = [];
        this.attendance = [];
        this.access = [];
        this.languages = [];
        this.cities = [];
        this.datePreset = 'all';
        this.dateDay = '';
        this.placeQuery = '';
        this.page = 1;
        refresh(this);
      },

      setPage(page) {
        this.page = Math.max(1, page);
        refresh(this);
        document.getElementById('events-catalog')?.scrollIntoView({
          behavior: isMotionReduce() ? 'auto' : 'smooth',
        });
      },

      onViewKeydown(event) {
        const index = CATALOGUE_VIEWS.indexOf(this.view);
        let next: CatalogueView | undefined;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          next = CATALOGUE_VIEWS[(index + 1) % CATALOGUE_VIEWS.length];
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          next =
            CATALOGUE_VIEWS[
              (index - 1 + CATALOGUE_VIEWS.length) % CATALOGUE_VIEWS.length
            ];
        } else if (event.key === 'Home') {
          next = CATALOGUE_VIEWS[0];
        } else if (event.key === 'End') {
          next = CATALOGUE_VIEWS[CATALOGUE_VIEWS.length - 1];
        }

        if (next == null || next === this.view) {
          return;
        }

        event.preventDefault();
        this.setView(next);
        this.$nextTick(() => {
          this.$refs[next]?.focus();
        });
      },
    };
  });
};

const teasersFor = (
  page: IndexedPreEvent[],
  labels: CatalogueCopy | undefined,
): CatalogueTeaser[] => {
  if (labels == null) {
    return [];
  }

  let month: string | undefined;
  return page.map((item) => {
    const heading =
      item.record.month != null && item.record.month !== month
        ? monthGroupLabel(item.record.month, labels.lang)
        : '';
    if (item.record.month != null) {
      month = item.record.month;
    }

    const start = parseWallClock(item.event.startDate);
    const when =
      start == null
        ? ''
        : formatWhen(
            start,
            parseWallClock(item.event.endDate),
            item.event.timezone,
            labels,
            labels.lang,
          );

    return toTeaser(item.event, when, heading, labels.lang, labels.and);
  });
};

const selectionOf = (store: PreEventCatalogueData): CatalogueSelection => ({
  theme: store.theme,
  types: store.types,
  attendance: store.attendance,
  access: store.access,
  languages: store.languages,
  cities: store.cities,
  datePreset: DATE_PRESETS.find((preset) => preset === store.datePreset) ?? 'all',
  dateDay: store.dateDay,
});

const cloneSelection = (selection: CatalogueSelection): CatalogueSelection => ({
  ...selection,
  types: [...selection.types],
  attendance: [...selection.attendance],
  access: [...selection.access],
  languages: [...selection.languages],
  cities: [...selection.cities],
});

const snapshotOf = (store: PreEventCatalogueData): FilterSnapshot => ({
  ...cloneSelection(selectionOf(store)),
  placeQuery: store.placeQuery,
});

const restoreSnapshot = (
  store: PreEventCatalogueData,
  snapshot: FilterSnapshot,
): void => {
  const { placeQuery, ...selection } = snapshot;
  const cloned = cloneSelection(selection);
  store.theme = cloned.theme;
  store.types = cloned.types;
  store.attendance = cloned.attendance;
  store.access = cloned.access;
  store.languages = cloned.languages;
  store.cities = cloned.cities;
  store.datePreset = cloned.datePreset;
  store.dateDay = cloned.dateDay;
  store.placeQuery = placeQuery;
};
